import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "./api";
import { useAuth } from "./AuthContext";

// Effective spoiler delay (seconds) for live game data: the signed-in user's
// per-account override (push_prefs.game_delay_sec) falls back to the owner's
// global setting. 0 = no delay.
export function useGameDelay() {
    const { user } = useAuth();
    const { data: settings } = useQuery({
        queryKey: ["settings"],
        queryFn: () => api.get("/settings").then((r) => r.data),
        staleTime: 60 * 1000,
    });
    const { data: account } = useQuery({
        queryKey: ["account"],
        queryFn: () => api.get("/account").then((r) => r.data),
        enabled: !!user,
        staleTime: 60 * 1000,
    });
    const clamp = (n) => Math.max(0, Math.min(300, Number(n) || 0));
    const global = clamp(settings?.game_delay_sec || 0);
    const override = account?.profile?.push_prefs?.game_delay_sec;
    return override === undefined || override === null ? global : clamp(override);
}

// Hold back a live-updating value by `delaySec` seconds. Buffers timestamped
// snapshots and promotes the most recent one that is at least `delaySec` old, so
// the Game Centre shows the game as it stood `delaySec` ago (no spoilers).
export function useDelayedSnapshot(data, delaySec, active, resetKey) {
    const bufRef = useRef([]);
    const [shown, setShown] = useState(data);
    // Reset the buffer when the subject changes (e.g. switching games) so a new
    // game never briefly shows the previously buffered game's snapshot.
    useEffect(() => {
        bufRef.current = [];
        setShown(data);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetKey]);
    useEffect(() => {
        if (data === undefined) return;
        if (!active || !delaySec) {
            bufRef.current = [];
            setShown(data);
            return;
        }
        bufRef.current.push({ t: Date.now(), v: data });
        setShown((s) => (s === undefined ? data : s)); // baseline until buffer matures
    }, [data, active, delaySec]);
    useEffect(() => {
        if (!active || !delaySec) return;
        const iv = setInterval(() => {
            const cutoff = Date.now() - delaySec * 1000;
            const buf = bufRef.current;
            let latest = null;
            const future = [];
            for (const s of buf) {
                if (s.t <= cutoff) latest = s;
                else future.push(s);
            }
            if (latest) {
                setShown(latest.v);
                bufRef.current = [latest, ...future];
            }
        }, 1000);
        return () => clearInterval(iv);
    }, [active, delaySec]);
    return !active || !delaySec ? data : shown;
}
